import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Briefcase, CheckCircle, Clock, AlertCircle, Shield, LogOut,
  Search, HelpCircle, Settings, Bell, CreditCard, Database, Loader2, BarChart2, Mail,
  TrendingUp, Star, ChevronRight, Megaphone, Image, Menu, X, Activity,
  LineChart
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AdminAnalyticsCharts from '../components/AdminAnalyticsCharts';
import ConversionFunnelChart from '../components/ConversionFunnelChart';
import Notifications from '../components/Notifications';

const SECTIONS = ['overview', 'users', 'jobs', 'subscriptions', 'conversion', 'quickactions'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, API, language } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('users');
  const [backupLoading, setBackupLoading] = useState(false);
  const [forceReminders, setForceReminders] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      const response = await axios.get(`${API}/admin/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = response.data;
      // Normalize nested API response to flat fields used throughout the dashboard
      setStats({
        ...d,
        total_users: d.users?.total ?? 0,
        total_clients: d.users?.clients ?? 0,
        total_professionals: d.users?.professionals ?? 0,
        active_subscriptions: d.users?.active_subscriptions ?? 0,
        trial_users: d.users?.trial_users ?? 0,
        new_users_month: d.users?.new_this_month ?? 0,
        new_users_week: d.users?.new_this_week ?? 0,
        verified_professionals: d.verifications?.approved ?? 0,
        pending_verifications: (d.verifications?.pending ?? 0) + (d.verifications?.pending_review ?? 0),
        total_jobs: d.jobs?.total ?? 0,
        open_jobs: d.jobs?.open ?? 0,
        assigned_jobs: d.jobs?.assigned ?? 0,
        completed_jobs: d.jobs?.completed ?? 0,
        cancelled_jobs: 0,
        jobs_this_week: d.jobs?.new_this_month ?? 0,
        total_bids: 0,
        accepted_bids: 0,
        total_messages: 0,
        mrr: d.mrr ?? d.revenue?.mrr ?? 0,
        arr: d.arr ?? d.revenue?.arr ?? 0,
        total_revenue: d.revenue?.total_chf ?? 0,
        revenue_this_month: d.revenue?.this_month_chf ?? 0,
        paid_active_subscriptions: d.revenue?.active_paid_subscriptions ?? 0,
        renewals_next_30_days: d.revenue?.renewals_next_30_days_chf ?? 0,
        renewals_next_12_months: d.revenue?.renewals_next_12_months_chf ?? 0,
        yearly_subscribers: d.revenue?.yearly_subscribers ?? 0,
        monthly_subscribers: d.revenue?.monthly_subscribers ?? 0,
        cancelled_subscriptions: 0,
        declined_subscriptions: 0,
        verification_rate: d.verifications?.total > 0 ? Math.round((d.verifications.approved / d.verifications.total) * 100) : 0,
        job_completion_rate: d.jobs?.total > 0 ? Math.round((d.jobs.completed / d.jobs.total) * 100) : 0,
        subscription_conversion: d.users?.total > 0 ? Math.round(((d.users?.active_subscriptions ?? 0) / d.users.total) * 100) : 0,
        bid_acceptance_rate: 0,
        support_open_tickets: d.support?.open_tickets ?? 0
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        logout();
      } else {
        toast.error('Failed to load statistics');
        console.error('Statistics error:', error.response?.data || error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunBackup = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('helply_token');
      const response = await axios.post(`${API}/admin/backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Backup completed! ${response.data.total_documents} documents saved.`);
    } catch (error) {
      toast.error('Backup failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const nav = {
    en: {
      overview: 'Overview',
      users: 'Users',
      jobs: 'Jobs',
      subscriptions: 'Subscriptions',
      conversion: 'Conversion Rates',
      quickactions: 'Quick Actions',
    },
    de: {
      overview: 'Übersicht',
      users: 'Benutzer',
      jobs: 'Aufträge',
      subscriptions: 'Abonnements',
      conversion: 'Konversionsraten',
      quickactions: 'Schnellaktionen',
    }
  };
  const navLabels = nav[language] || nav.en;

  const sidebarItems = [
    { id: 'overview', label: navLabels.overview, icon: Activity },
    { id: 'analytics', label: language === 'de' ? 'Analysen' : 'Analytics', icon: LineChart },
    { id: 'users', label: navLabels.users, icon: Users },
    { id: 'jobs', label: navLabels.jobs, icon: Briefcase },
    { id: 'subscriptions', label: navLabels.subscriptions, icon: Star },
    { id: 'conversion', label: navLabels.conversion, icon: TrendingUp },
    { id: 'quickactions', label: navLabels.quickactions, icon: BarChart2 },
  ];

  const externalLinks = [
    { label: language === 'en' ? 'Verifications' : language === 'it' ? 'Verifiche' : language === 'fr' ? 'Vérifications' : 'Verifizierungen', path: '/admin/verifications', icon: CheckCircle, badge: stats?.pending_verifications },
    { label: language === 'en' ? 'User Management' : language === 'it' ? 'Gestione Utenti' : language === 'fr' ? 'Gestion Utilisateurs' : 'Benutzerverwaltung', path: '/admin/users', icon: Users },
    { label: language === 'en' ? 'User Activity' : language === 'it' ? 'Attività Utenti' : language === 'fr' ? 'Activité Utilisateurs' : 'Benutzeraktivität', path: '/admin/user-activity', icon: Activity },
    { label: language === 'en' ? 'Job Management' : language === 'it' ? 'Gestione Lavori' : language === 'fr' ? 'Gestion Emplois' : 'Auftragsverwaltung', path: '/admin/jobs', icon: Briefcase },
    { label: language === 'en' ? 'Support Tickets' : language === 'it' ? 'Ticket Supporto' : language === 'fr' ? 'Tickets Support' : 'Support-Tickets', path: '/admin/support', icon: HelpCircle },
    { label: language === 'en' ? 'Announcements' : language === 'it' ? 'Annunci' : language === 'fr' ? 'Annonces' : 'Ankündigungen', path: '/admin/announcements', icon: Megaphone },
    { label: language === 'en' ? 'Ads & Marketing' : language === 'it' ? 'Pubblicità' : language === 'fr' ? 'Publicités' : 'Werbung', path: '/admin/ads', icon: Image },
    { label: language === 'en' ? 'Audit Logs' : language === 'it' ? 'Log Audit' : language === 'fr' ? 'Journaux d\'audit' : 'Prüfprotokolle', path: '/admin/audit-logs', icon: Shield },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  const StatCard = ({ label, value, color = 'text-gray-900', bg, testId }) => {
    const resolvedTestId = testId || `admin-stat-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      <Card className={`hover-lift ${bg || ''}`} data-testid={resolvedTestId}>
        <CardContent className="pt-4 pb-3">
          <p className="text-xs text-gray-500 font-medium mb-1 leading-tight" data-testid={`${resolvedTestId}-label`}>{label}</p>
          <p className={`text-2xl font-bold ${color}`} data-testid={`${resolvedTestId}-value`}>{value}</p>
        </CardContent>
      </Card>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'analytics':
        return <AdminAnalyticsCharts />;

      case 'overview':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              {language === 'en' ? 'Platform Overview' : 'Plattform-Übersicht'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard testId="admin-overview-total-users" label={language === 'en' ? 'Total Users' : 'Benutzer'} value={stats?.total_users || 0} color="text-gray-900" />
              <StatCard testId="admin-overview-total-jobs" label={language === 'en' ? 'Total Jobs' : 'Aufträge'} value={stats?.total_jobs || 0} color="text-blue-600" />
              <StatCard testId="admin-overview-paid-subscriptions" label={language === 'en' ? 'Paid Subs' : 'Bezahlte Abos'} value={stats?.paid_active_subscriptions || 0} color="text-green-600" />
              <StatCard testId="admin-overview-total-revenue" label={language === 'en' ? 'Collected Revenue' : 'Bezahlter Umsatz'} value={`CHF ${stats?.total_revenue?.toFixed(2) || '0.00'}`} color="text-emerald-700" bg="bg-gradient-to-br from-green-50 to-green-100 border-green-200" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard testId="admin-overview-mrr" label="MRR" value={`CHF ${stats?.mrr?.toFixed(2) || '0.00'}`} color="text-green-700" bg="bg-gradient-to-br from-green-50 to-green-100 border-green-200" />
              <StatCard testId="admin-overview-arr" label={language === 'en' ? 'ARR' : 'Jahresumsatz'} value={`CHF ${stats?.arr?.toFixed(2) || '0.00'}`} color="text-green-700" bg="bg-gradient-to-br from-green-50 to-green-100 border-green-200" />
              <StatCard testId="admin-overview-new-users" label={language === 'en' ? 'New Users (30d)' : 'Neue (30T)'} value={stats?.new_users_month || 0} color="text-cyan-600" />
              <StatCard testId="admin-overview-pending-verifications" label={language === 'en' ? 'Pending Verif.' : 'Ausstehend'} value={stats?.pending_verifications || 0} color="text-orange-600" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard testId="admin-overview-renewals-30-days" label={language === 'en' ? 'Expected renewals — next 30 days' : 'Erwartete Verlängerungen — 30 Tage'} value={`CHF ${stats?.renewals_next_30_days?.toFixed(2) || '0.00'}`} color="text-blue-700" />
              <StatCard testId="admin-overview-renewals-12-months" label={language === 'en' ? 'Expected renewals — next 12 months' : 'Erwartete Verlängerungen — 12 Monate'} value={`CHF ${stats?.renewals_next_12_months?.toFixed(2) || '0.00'}`} color="text-blue-700" />
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/user-activity?activity=paid')}
              className="gap-2"
              data-testid="admin-view-payments-button"
            >
              <CreditCard className="w-4 h-4" />
              {language === 'en' ? 'View payment details' : 'Zahlungsdetails anzeigen'}
            </Button>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              {language === 'en' ? 'User Statistics' : 'Benutzerstatistiken'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard label={language === 'en' ? 'Total Users' : 'Gesamt'} value={stats?.total_users || 0} />
              <StatCard label={language === 'en' ? 'Clients' : 'Kunden'} value={stats?.total_clients || 0} color="text-blue-600" />
              <StatCard label={language === 'en' ? 'Professionals' : 'Fachkräfte'} value={stats?.total_professionals || 0} color="text-purple-600" />
              <StatCard label={language === 'en' ? 'Verified Pros' : 'Verifizierte'} value={stats?.verified_professionals || 0} color="text-green-600" />
              <StatCard label={language === 'en' ? 'New (7d)' : 'Neu (7T)'} value={stats?.new_users_week || 0} color="text-cyan-600" />
              <StatCard label={language === 'en' ? 'New (30d)' : 'Neu (30T)'} value={stats?.new_users_month || 0} color="text-cyan-700" />
            </div>
            <div className="flex flex-wrap gap-3 mt-2">
              <Button onClick={() => navigate('/admin/users')} className="gap-2">
                <Users className="w-4 h-4" />
                {language === 'en' ? 'Manage Users' : 'Benutzer verwalten'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/verifications')} className="gap-2">
                <CheckCircle className="w-4 h-4" />
                {language === 'en' ? 'Verifications' : 'Verifizierungen'}
                {stats?.pending_verifications > 0 && (
                  <Badge variant="destructive" className="ml-1">{stats.pending_verifications}</Badge>
                )}
              </Button>
            </div>
          </div>
        );

      case 'jobs':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-600" />
              {language === 'en' ? 'Job Statistics' : 'Auftragsstatistiken'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard label={language === 'en' ? 'Total Jobs' : 'Gesamt'} value={stats?.total_jobs || 0} />
              <StatCard label={language === 'en' ? 'Open' : 'Offen'} value={stats?.open_jobs || 0} color="text-yellow-600" />
              <StatCard label={language === 'en' ? 'Assigned' : 'Zugewiesen'} value={stats?.assigned_jobs || 0} color="text-blue-600" />
              <StatCard label={language === 'en' ? 'Completed' : 'Abgeschlossen'} value={stats?.completed_jobs || 0} color="text-green-600" />
              <StatCard label={language === 'en' ? 'Cancelled' : 'Abgebrochen'} value={stats?.cancelled_jobs || 0} color="text-red-600" />
              <StatCard label={language === 'en' ? 'This Week' : 'Diese Woche'} value={stats?.jobs_this_week || 0} color="text-cyan-600" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label={language === 'en' ? 'Total Bids' : 'Angebote'} value={stats?.total_bids || 0} color="text-indigo-600" />
              <StatCard label={language === 'en' ? 'Accepted Bids' : 'Angenommene'} value={stats?.accepted_bids || 0} color="text-green-600" />
              <StatCard label={language === 'en' ? 'Messages' : 'Nachrichten'} value={stats?.total_messages || 0} color="text-gray-600" />
            </div>
            <Button onClick={() => navigate('/admin/jobs')} className="gap-2">
              <Briefcase className="w-4 h-4" />
              {language === 'en' ? 'Manage Jobs' : 'Aufträge verwalten'}
            </Button>
          </div>
        );

      case 'subscriptions':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              {language === 'en' ? 'Subscription Statistics' : 'Abonnementstatistiken'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label={language === 'en' ? 'Active' : 'Aktiv'} value={stats?.active_subscriptions || 0} color="text-green-600" bg="bg-gradient-to-br from-green-50 to-green-100 border-green-200" />
              <StatCard label={language === 'en' ? 'Trial Users' : 'Testbenutzer'} value={stats?.trial_users || 0} color="text-blue-600" />
              <StatCard label={language === 'en' ? 'Cancelled' : 'Gekündigt'} value={stats?.cancelled_subscriptions || 0} color="text-red-600" />
              <StatCard label={language === 'en' ? 'Declined' : 'Abgelehnt'} value={stats?.declined_subscriptions || 0} color="text-orange-600" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label={language === 'en' ? 'Yearly Subs' : 'Jährl. Abos'} value={stats?.yearly_subscribers || 0} color="text-purple-600" />
              <StatCard label={language === 'en' ? 'Monthly Subs' : 'Monatl. Abos'} value={stats?.monthly_subscribers || 0} color="text-blue-600" />
              <StatCard label="MRR" value={`CHF ${stats?.mrr?.toFixed(2) || '0.00'}`} color="text-green-700" bg="bg-gradient-to-br from-green-50 to-green-100 border-green-200" />
              <StatCard label="ARR" value={`CHF ${stats?.arr?.toFixed(2) || '0.00'}`} color="text-green-800" bg="bg-gradient-to-br from-green-50 to-green-100 border-green-200" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard testId="admin-subscriptions-renewals-30-days" label={language === 'en' ? 'Expected renewals — next 30 days' : 'Erwartete Verlängerungen — 30 Tage'} value={`CHF ${stats?.renewals_next_30_days?.toFixed(2) || '0.00'}`} color="text-blue-700" />
              <StatCard testId="admin-subscriptions-renewals-12-months" label={language === 'en' ? 'Expected renewals — next 12 months' : 'Erwartete Verlängerungen — 12 Monate'} value={`CHF ${stats?.renewals_next_12_months?.toFixed(2) || '0.00'}`} color="text-blue-700" />
            </div>
          </div>
        );

      case 'conversion':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              {language === 'en' ? 'Conversion Rates' : 'Konversionsraten'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-indigo-700 font-medium">{language === 'en' ? 'Verification Rate' : 'Verifizierungsrate'}</p>
                  <p className="text-3xl font-bold text-indigo-800">{stats?.verification_rate || 0}%</p>
                  <p className="text-xs text-indigo-600 mt-1">{language === 'en' ? 'of professionals verified' : 'der Fachkräfte verifiziert'}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-teal-700 font-medium">{language === 'en' ? 'Job Completion' : 'Auftragsabschluss'}</p>
                  <p className="text-3xl font-bold text-teal-800">{stats?.job_completion_rate || 0}%</p>
                  <p className="text-xs text-teal-600 mt-1">{language === 'en' ? 'of jobs completed' : 'der Aufträge abgeschlossen'}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-amber-700 font-medium">{language === 'en' ? 'Sub Conversion' : 'Abo-Konversion'}</p>
                  <p className="text-3xl font-bold text-amber-800">{stats?.subscription_conversion || 0}%</p>
                  <p className="text-xs text-amber-600 mt-1">{language === 'en' ? 'users subscribed' : 'Benutzer abonniert'}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-rose-700 font-medium">{language === 'en' ? 'Client:Pro Ratio' : 'Kunden:Pro'}</p>
                  <p className="text-3xl font-bold text-rose-800">{stats?.client_to_pro_ratio || 0}:1</p>
                  <p className="text-xs text-rose-600 mt-1">{language === 'en' ? 'clients per professional' : 'Kunden je Fachkraft'}</p>
                </CardContent>
              </Card>
            </div>
            <ConversionFunnelChart />
          </div>
        );

      case 'quickactions':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-blue-600" />
              {language === 'en' ? 'Quick Actions' : 'Schnellaktionen'}
            </h2>

            {/* Quick Search */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{language === 'en' ? 'Quick Search' : 'Schnellsuche'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder={language === 'en' ? 'Search users or jobs...' : 'Benutzer oder Aufträge suchen...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && searchQuery.trim()) {
                          navigate(searchType === 'users' ? `/admin/users?search=${searchQuery}` : `/admin/jobs?search=${searchQuery}`);
                        }
                      }}
                      className="pl-10"
                    />
                  </div>
                  <Select value={searchType} onValueChange={setSearchType}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="users">{language === 'en' ? 'Users' : 'Benutzer'}</SelectItem>
                      <SelectItem value="jobs">{language === 'en' ? 'Jobs' : 'Aufträge'}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => { if (searchQuery.trim()) navigate(searchType === 'users' ? `/admin/users?search=${searchQuery}` : `/admin/jobs?search=${searchQuery}`); }}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Action Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {externalLinks.map((item) => (
                <Card key={item.path} className="hover-lift cursor-pointer" onClick={() => navigate(item.path)}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                          <item.icon className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-800">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.badge > 0 && <Badge variant="destructive">{item.badge}</Badge>}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Backup */}
              <Card className="hover-lift">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                      <Database className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="font-medium text-gray-800">{language === 'en' ? 'Database Backup' : 'Datenbank-Backup'}</span>
                  </div>
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-sm"
                    onClick={handleRunBackup}
                    disabled={backupLoading}
                  >
                    {backupLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'en' ? 'Running...' : 'Läuft...'}</>
                    ) : (
                      <><Database className="w-4 h-4 mr-2" />{language === 'en' ? 'Run Backup' : 'Backup ausführen'}</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Send Subscription Reminders */}
              <Card className="hover-lift border-orange-200">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <span className="font-medium text-gray-800 block">{language === 'en' ? 'Subscription Reminders' : 'Abo-Erinnerungen'}</span>
                      <span className="text-xs text-gray-500">{language === 'en' ? 'Email all trial/expired users' : 'Alle Test-/abgelaufenen Nutzer'}</span>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mb-3 text-xs text-gray-600 cursor-pointer" data-testid="force-reminders-label">
                    <input
                      type="checkbox"
                      checked={forceReminders}
                      onChange={(e) => setForceReminders(e.target.checked)}
                      data-testid="force-reminders-checkbox"
                      className="rounded border-gray-300"
                    />
                    {language === 'en' ? 'Ignore 7-day cooldown (resend to everyone now)' : '7-Tage-Sperre ignorieren (jetzt an alle senden)'}
                  </label>
                  <Button
                    className="w-full bg-orange-600 hover:bg-orange-700 text-sm"
                    data-testid="send-reminders-btn"
                    disabled={reminderLoading}
                    onClick={async () => {
                      setReminderLoading(true);
                      try {
                        const token = localStorage.getItem('helply_token');
                        const res = await fetch(`${API}/admin/send-subscription-reminders?force=${forceReminders}`, {
                          method: 'POST', headers: { Authorization: `Bearer ${token}` }
                        });
                        const d = await res.json();
                        toast.success(`Sent: ${d.sent} | Skipped: ${d.skipped} | Failed: ${d.failed}`);
                        if (d.failed_details?.length > 0) {
                          console.error('Reminder failures:', d.failed_details);
                          const preview = d.failed_details.slice(0, 3).map(f => f.email).join(', ');
                          const more = d.failed_details.length > 3 ? ` +${d.failed_details.length - 3} more (see console)` : '';
                          toast.error(`Failed for: ${preview}${more}`);
                        }
                      } catch (e) { toast.error('Failed to send reminders'); }
                      finally { setReminderLoading(false); }
                    }}
                  >
                    {reminderLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'en' ? 'Sending...' : 'Senden...'}</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" />{language === 'en' ? 'Send Reminders' : 'Erinnerungen senden'}</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="admin-dashboard">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Shield className="w-6 h-6 text-blue-600" />
            <h1 className="text-lg font-bold">{language === 'en' ? 'Admin Dashboard' : 'Admin-Dashboard'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Notifications />
            <Button variant="outline" size="sm" onClick={() => navigate('/settings')} data-testid="settings-btn" className="hidden sm:flex gap-1">
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline">{language === 'en' ? 'Settings' : 'Einstellungen'}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn" className="gap-1">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'Logout' : 'Abmelden'}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className={`
          fixed inset-y-0 left-0 z-40 w-56 bg-white border-r border-gray-200 pt-16 transform transition-transform duration-200 md:static md:translate-x-0 md:pt-0 md:flex-shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <nav className="p-3 space-y-1">
            <p className="text-xs font-semibold text-gray-400 uppercase px-3 py-2">
              {language === 'en' ? 'Dashboard' : 'Dashboard'}
            </p>
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeSection === item.id
                    ? 'bg-blue-50 text-blue-700 border border-blue-100'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                data-testid={`sidebar-${item.id}`}
              >
                <item.icon className={`w-4 h-4 flex-shrink-0 ${activeSection === item.id ? 'text-blue-600' : 'text-gray-400'}`} />
                {item.label}
              </button>
            ))}

            <div className="pt-3">
              <p className="text-xs font-semibold text-gray-400 uppercase px-3 py-2">
                {language === 'en' ? 'Management' : 'Verwaltung'}
              </p>
              {externalLinks.map((item) => (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all"
                >
                  <item.icon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{item.badge}</span>
                  )}
                </button>
              ))}
            </div>
          </nav>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 min-w-0">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
